import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  Suspense,
} from "react";
import { ThemeProvider, CssBaseline, createTheme } from "@mui/material";
import ProtectedRoute from "./ProtectedRoute";
import { useLocation } from "react-router-dom";
import Homepage from "./Homepage";
import ApplyForm from "./ApplyForm";
import ApplyPage from "./ApplyPage";
import NavBar from "./NavBar";
import SignInSignUp from "./SignInSignUp";
import VerifyEmail from "./VerifyEmail";
import ForgotPassword from "./ForgotPassword";
import ResetPasswordToken from "./ResetPasswordToken";
import AdminDashboard from "./AdminDashboard";
import CandidatePortalWrapper from "./CandidatePortal"; // error boundary version
import SettingsLayout from "./admin/SettingsLayout";
import StatusesPage from "./admin/StatusesPage";
import EmailSettings from "./admin/settings/email";
import GoogleCalendarSettingsPage from "./admin/settings/GoogleCalendarSettingsPage";
import UsersPage from "./admin/users/UsersPage";
import RolesPage from "./admin/roles/RolesPage";
import PermissionsPage from "./admin/permissions/PermissionsPage";
import JobOrders from "./admin/JobOrders";
import Jobs from "./admin/Jobs";
import Organizations from "./admin/Organizations";
import Candidates from "./admin/Candidates";
import JobOrderDetail from "./admin/JobOrderDetail";
import JobOrderCandidates from "./admin/JobOrderCandidates";
import CandidateDetail from "./admin/CandidateDetail";
import JobBoard from "./JobBoard";
import ThankYouPage from "./ThankYouPage";
import Questions from "./admin/Questions";
import PdfTextExtractor from "./PdfTextExtractor";
// import { theme as ftlTheme } from "./theme";
import { getAppTheme } from "./theme";
import SessionManager from "./components/SessionManager";
import LocationsPage from "./admin/settings/LocationsPage";
import JobTypesPage from "./admin/settings/JobTypesPage";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

// Auth context for fetchWithAuth and signOut
export const AuthContext = createContext();
export function useAuth() {
  return useContext(AuthContext);
}

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload.exp) return false;
    return Date.now() / 1000 > payload.exp;
  } catch {
    return true;
  }
}

function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(() => {
    // Optionally persist auth state with localStorage
    const stored = localStorage.getItem("ftl_auth");
    return stored ? JSON.parse(stored) : null;
  });
  const isAuthenticated = !!auth;
  const handleAuth = (data) => {
    // Decode JWT to extract user info (including is_applicant)
    let user = undefined;
    try {
      const payload = JSON.parse(atob(data.access_token.split(".")[1]));
      user = {
        email: payload.sub,
        is_applicant: payload.is_applicant,
        is_superuser: payload.is_superuser,
        permissions: payload.permissions,
        ...payload,
      };
    } catch {}
    const authObj = { ...data, user };
    setAuth(authObj);
    localStorage.setItem("ftl_auth", JSON.stringify(authObj));
  };
  const handleSignOut = (redirectPath) => {
    setAuth(null);
    localStorage.removeItem("ftl_auth");
    // Defensive: ensure redirectPath is a string path
    let path;
    if (redirectPath && typeof redirectPath === "object") {
      path = (redirectPath.pathname || "") + (redirectPath.search || "");
    } else if (redirectPath) {
      path = String(redirectPath);
    } else {
      path = window.location.pathname + window.location.search;
    }
    // Prevent infinite redirect loop: never allow /signin as redirect param
    if (path.startsWith("/signin")) {
      path = "/";
    }
    // Always redirect to /signin (never /login)
    navigate(`/signin?redirect=${encodeURIComponent(path)}`);
  };
  // Helper for authenticated fetch with auto-logout on 401/expired
  const fetchWithAuth = async (url, options = {}) => {
    const token = auth?.access_token;
    if (!token || isTokenExpired(token)) {
      handleSignOut(window.location.pathname + window.location.search);
      throw new Error("Session expired. Please sign in again.");
    }
    const res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        "Content-Type": options.body ? "application/json" : undefined,
      },
    });
    if (res.status === 401) {
      handleSignOut(window.location.pathname + window.location.search);
      throw new Error("Session expired. Please sign in again.");
    }
    if (res.status === 403) {
      // Show a user-friendly error for forbidden
      throw new Error(
        "You are not authorized to perform this action. Please contact your system administrator."
      );
    }
    return res;
  };
  useEffect(() => {
    window.fetchWithAuth = fetchWithAuth;
  }, [auth]);
  return (
    <AuthContext.Provider
      value={{
        auth,
        isAuthenticated,
        handleAuth,
        handleSignOut,
        fetchWithAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// (No stray code below this line)

function AppRoutes({ toggleTheme }) {
  const { auth, isAuthenticated, handleAuth, handleSignOut } = useAuth();
  const location = useLocation();
  const hideNav = location.pathname === "/pdf-text-extractor";
  return (
    <>
      {!hideNav && (
        <NavBar isAuthenticated={isAuthenticated} authToken={auth?.access_token} onSignOut={handleSignOut} onToggleTheme={toggleTheme} />
      )}
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/jobs" element={<JobBoard />} />
          <Route path="/job/:id" element={<JobOrderDetail />} />
          <Route path="/apply/:id" element={<ApplyPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/pdf-text-extractor" element={<PdfTextExtractor />} />
          <Route
            path="/signin"
            element={
              <SignInSignUp
                onAuth={(data) => {
                  handleAuth(data);
                  // Get redirect param from URL
                  const params = new URLSearchParams(window.location.search);
                  const redirectPath = params.get("redirect");
                  let user;
                  try {
                    const payload = JSON.parse(
                      atob(data.access_token.split(".")[1])
                    );
                    user = payload;
                  } catch {}
                  // Only redirect if redirectPath is present, not /signin, and not empty
                  if (redirectPath && redirectPath !== "/signin") {
                    window.location.replace(redirectPath);
                  } else if (user && user.is_applicant) {
                    window.location.replace("/candidate");
                  } else {
                    window.location.replace("/admin");
                  }
                }}
              />
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-orders"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <JobOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-orders/:id"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <JobOrderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-orders/:id/candidates"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <JobOrderCandidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Jobs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Organizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/questions"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Questions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidates"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Candidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidates/:id"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CandidateDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  Settings Home (choose an option from sidebar)
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/locations"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <LocationsPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/job-types"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <JobTypesPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/statuses"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <StatusesPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/email"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <EmailSettings />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/google-calendar"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <GoogleCalendarSettingsPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/roles"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <RolesPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings/permissions"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SettingsLayout>
                  <PermissionsPage />
                </SettingsLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidate"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CandidatePortalWrapper />
              </ProtectedRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPasswordToken />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
        </Routes>
      </Suspense>
    </>
  );
}

// function App() {
//   return (
//     <ThemeProvider theme={muiTheme}>
//       <CssBaseline />
//       <BrowserRouter>
//         <SessionManager>
//           <AuthProvider>
//             <AppRoutes />
//           </AuthProvider>
//         </SessionManager>
//       </BrowserRouter>
//     </ThemeProvider>
//   );
// }

function App() {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem("ftl_theme_mode");
    return saved === "dark" || saved === "light" ? saved : "light";
  });

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("ftl_theme_mode", next);
      return next;
    });
  };

  // const muiTheme = useMemo(
  //   () =>
  //     createTheme({
  //       ...ftlTheme,
  //       palette: {
  //         ...(ftlTheme.palette || {}),
  //         mode,
  //       },
  //     }),
  //   [mode]
  // );

  const muiTheme = useMemo(() => createTheme(getAppTheme(mode)), [mode]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <BrowserRouter>
        <SessionManager>
          <AuthProvider>
            <AppRoutes toggleTheme={toggleTheme} />
          </AuthProvider>
        </SessionManager>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
